<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">All Deliveries</h1>
        <p class="gl-page-sub">Monitor and inspect every delivery in the system</p>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="gl-filter-bar">
      <div class="gl-filter-search">
        <svg class="gl-filter-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input class="gl-filter-input" type="text" v-model="filters.search" placeholder="Search deliveries…" @input="debounceSearch">
      </div>
      <select class="gl-filter-select" v-model="filters.status" @change="fetchDeliveries">
        <option value="">All Status</option>
        <option value="active">Active</option>
        <option value="pending">Pending</option>
        <option value="assigned">Assigned</option>
        <option value="on_the_way">On The Way</option>
        <option value="delivered">Delivered</option>
        <option value="failed">Failed</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <select class="gl-filter-select" v-model="filters.provider" @change="fetchDeliveries">
        <option value="">All Providers</option>
        <option value="inhouse">In-House</option>
        <option value="lalamove">Lalamove</option>
        <option value="grab">Grab</option>
      </select>
    </div>

    <!-- Table Card -->
    <div class="gl-card">
      <!-- Skeleton -->
      <template v-if="loading">
        <div class="gl-tbl-skel">
          <div class="gl-tbl-skel-head">
            <div class="gl-skel gl-skel-xs" v-for="i in 8" :key="i"></div>
          </div>
          <div v-for="i in 8" :key="i" class="gl-tbl-skel-row">
            <div class="gl-skel gl-skel-xs" style="width:44px"></div>
            <div class="gl-skel gl-skel-sm" style="flex:0.8"></div>
            <div class="gl-skel gl-skel-md" style="flex:1"></div>
            <div class="gl-skel gl-skel-md" style="flex:1"></div>
            <div class="gl-skel gl-skel-md" style="flex:1"></div>
            <div class="gl-skel-badge"></div>
            <div class="gl-skel-badge"></div>
            <div class="gl-skel gl-skel-sm" style="flex:0.7"></div>
            <div class="gl-skel-btn"></div>
          </div>
        </div>
      </template>

      <!-- Empty -->
      <div v-else-if="deliveries.length === 0" class="gl-empty">
        <div class="gl-empty-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
        </div>
        <div class="gl-empty-title">No deliveries found</div>
        <div class="gl-empty-sub">Try adjusting your filters</div>
      </div>

      <template v-else>
        <!-- Desktop Table -->
        <div class="gl-tbl-scroll">
          <table class="gl-tbl">
            <thead>
              <tr>
                <th>ID</th>
                <th>Order</th>
                <th>Customer</th>
                <th>Outlet</th>
                <th>Rider</th>
                <th>Provider</th>
                <th>Status</th>
                <th>Fee</th>
                <th>Dist</th>
                <th>Created</th>
                <th style="text-align:center">View</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in deliveries" :key="d.id">
                <td><span class="gl-id-badge">#{{ d.id }}</span></td>
                <td>
                  <span v-if="d.order" class="gl-id-badge">#{{ d.order.order_number }}</span>
                  <span v-else class="gl-tbl-sub">—</span>
                </td>
                <td>{{ d.order?.user?.name || '—' }}</td>
                <td>{{ d.order?.outlet?.name || '—' }}</td>
                <td>
                  <span v-if="d.rider">{{ d.rider.name }}</span>
                  <span v-else-if="d.driver_name">{{ d.driver_name }}</span>
                  <span v-else class="gl-tbl-sub">Unassigned</span>
                </td>
                <td><span class="gl-badge" :class="providerBadgeClass(d.provider)">{{ d.provider }}</span></td>
                <td><span class="gl-badge" :class="statusBadgeClass(d.status)">{{ formatStatus(d.status) }}</span></td>
                <td><strong>RM {{ formatCurrency(d.fee) }}</strong></td>
                <td class="gl-tbl-sub">{{ d.distance_km ? d.distance_km + ' km' : '—' }}</td>
                <td class="gl-tbl-sub">{{ formatDate(d.created_at) }}</td>
                <td style="text-align:center">
                  <button class="gl-icon-btn" @click="viewDelivery(d)" title="View Details">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Cards -->
        <div class="gl-mob-list">
          <div v-for="d in deliveries" :key="d.id" class="gl-mob-card">
            <div class="gl-dlv-mob-top">
              <div class="gl-dlv-mob-ids">
                <span class="gl-id-badge">#{{ d.id }}</span>
                <span v-if="d.order" class="gl-id-badge" style="background:#f0f3f2;color:#6b7f7b">#{{ d.order.order_number }}</span>
              </div>
              <span class="gl-badge" :class="statusBadgeClass(d.status)">{{ formatStatus(d.status) }}</span>
            </div>
            <div class="gl-dlv-mob-grid">
              <div class="gl-dlv-mob-cell">
                <div class="gl-mob-label">Customer</div>
                <div class="gl-mob-val">{{ d.order?.user?.name || '—' }}</div>
              </div>
              <div class="gl-dlv-mob-cell">
                <div class="gl-mob-label">Rider</div>
                <div class="gl-mob-val">{{ d.rider?.name || d.driver_name || 'Unassigned' }}</div>
              </div>
              <div class="gl-dlv-mob-cell">
                <div class="gl-mob-label">Provider</div>
                <span class="gl-badge" :class="providerBadgeClass(d.provider)">{{ d.provider }}</span>
              </div>
              <div class="gl-dlv-mob-cell">
                <div class="gl-mob-label">Fee</div>
                <div class="gl-mob-val gl-mob-val-em">RM {{ formatCurrency(d.fee) }}</div>
              </div>
              <div class="gl-dlv-mob-cell">
                <div class="gl-mob-label">Distance</div>
                <div class="gl-mob-val">{{ d.distance_km ? d.distance_km + ' km' : '—' }}</div>
              </div>
              <div class="gl-dlv-mob-cell">
                <div class="gl-mob-label">Time</div>
                <div class="gl-mob-val">{{ formatDate(d.created_at) }}</div>
              </div>
            </div>
            <button class="gl-mob-view-btn" @click="viewDelivery(d)">View Details</button>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.lastPage > 1" class="gl-pager">
          <div class="gl-pager-info">{{ pagination.total }} total</div>
          <div class="gl-pager-btns">
            <button class="gl-pager-btn" :disabled="pagination.currentPage === 1" @click="goToPage(pagination.currentPage - 1)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button v-for="page in paginationPages" :key="page" class="gl-pager-btn" :class="{ 'gl-pager-btn-active': page === pagination.currentPage, 'gl-pager-btn-dots': page === '...' }" @click="page !== '...' && goToPage(page)">{{ page }}</button>
            <button class="gl-pager-btn" :disabled="pagination.currentPage === pagination.lastPage" @click="goToPage(pagination.currentPage + 1)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- Detail Modal -->
    <div v-if="selectedDelivery" class="gl-modal-overlay" @click.self="selectedDelivery = null">
      <div class="gl-modal gl-modal-lg">
        <div class="gl-modal-header">
          <div>
            <div class="gl-modal-title">Delivery #{{ selectedDelivery.id }}</div>
            <div class="gl-modal-sub">
              <span class="gl-badge" :class="statusBadgeClass(selectedDelivery.status)">{{ formatStatus(selectedDelivery.status) }}</span>
            </div>
          </div>
          <button class="gl-modal-close" @click="selectedDelivery = null">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <div class="gl-detail-grid">
            <!-- Delivery Info -->
            <div>
              <div class="gl-detail-section-title">Delivery Info</div>
              <div class="gl-detail-rows">
                <div class="gl-detail-row">
                  <span>Provider</span>
                  <span class="gl-badge" :class="providerBadgeClass(selectedDelivery.provider)">{{ selectedDelivery.provider }}</span>
                </div>
                <div class="gl-detail-row">
                  <span>Fee</span>
                  <strong>RM {{ formatCurrency(selectedDelivery.fee) }}</strong>
                </div>
                <div class="gl-detail-row">
                  <span>Distance</span>
                  <span>{{ selectedDelivery.distance_km ? selectedDelivery.distance_km + ' km' : '—' }}</span>
                </div>
                <div class="gl-detail-row">
                  <span>Est. Time</span>
                  <span>{{ selectedDelivery.estimated_minutes ? selectedDelivery.estimated_minutes + ' min' : '—' }}</span>
                </div>
                <div class="gl-detail-row" v-if="selectedDelivery.tracking_url">
                  <span>Tracking</span>
                  <a :href="selectedDelivery.tracking_url" target="_blank" style="color:#900C27;font-weight:600">Open →</a>
                </div>
              </div>
            </div>
            <!-- Rider Info -->
            <div>
              <div class="gl-detail-section-title">Rider Info</div>
              <div v-if="selectedDelivery.rider" class="gl-detail-rows">
                <div class="gl-detail-row"><span>Name</span><strong>{{ selectedDelivery.rider.name }}</strong></div>
                <div class="gl-detail-row"><span>Phone</span><span>{{ selectedDelivery.rider.phone }}</span></div>
                <div class="gl-detail-row"><span>Vehicle</span><span>{{ selectedDelivery.rider.vehicle_type }} · {{ selectedDelivery.rider.vehicle_plate }}</span></div>
              </div>
              <div v-else-if="selectedDelivery.driver_name" class="gl-detail-rows">
                <div class="gl-detail-row"><span>Name</span><strong>{{ selectedDelivery.driver_name }} <span style="color:#9ca3af">(External)</span></strong></div>
                <div class="gl-detail-row"><span>Phone</span><span>{{ selectedDelivery.driver_phone || '—' }}</span></div>
                <div class="gl-detail-row"><span>Vehicle</span><span>{{ selectedDelivery.vehicle_type }} · {{ selectedDelivery.vehicle_plate }}</span></div>
              </div>
              <div v-else class="gl-detail-empty">No rider assigned yet</div>
            </div>
          </div>

          <!-- Timeline -->
          <div class="gl-detail-section-title" style="margin-top:20px">Timeline</div>
          <div class="gl-timeline">
            <div class="gl-timeline-item" v-if="selectedDelivery.created_at">
              <div class="gl-timeline-dot gl-dot-gray"></div>
              <div class="gl-timeline-content">
                <span class="gl-badge gl-badge-gray">Created</span>
                <span class="gl-timeline-time">{{ formatDateTime(selectedDelivery.created_at) }}</span>
              </div>
            </div>
            <div class="gl-timeline-item" v-if="selectedDelivery.assigned_at">
              <div class="gl-timeline-dot gl-dot-blue"></div>
              <div class="gl-timeline-content">
                <span class="gl-badge gl-badge-info">Assigned</span>
                <span class="gl-timeline-time">{{ formatDateTime(selectedDelivery.assigned_at) }}</span>
              </div>
            </div>
            <div class="gl-timeline-item" v-if="selectedDelivery.picked_up_at">
              <div class="gl-timeline-dot gl-dot-teal"></div>
              <div class="gl-timeline-content">
                <span class="gl-badge gl-badge-teal">Picked Up</span>
                <span class="gl-timeline-time">{{ formatDateTime(selectedDelivery.picked_up_at) }}</span>
              </div>
            </div>
            <div class="gl-timeline-item" v-if="selectedDelivery.delivered_at">
              <div class="gl-timeline-dot gl-dot-green"></div>
              <div class="gl-timeline-content">
                <span class="gl-badge gl-badge-green">Delivered</span>
                <span class="gl-timeline-time">{{ formatDateTime(selectedDelivery.delivered_at) }}</span>
              </div>
            </div>
            <div class="gl-timeline-item" v-if="selectedDelivery.failed_at">
              <div class="gl-timeline-dot gl-dot-red"></div>
              <div class="gl-timeline-content">
                <span class="gl-badge gl-badge-red">Failed</span>
                <span class="gl-timeline-time">{{ formatDateTime(selectedDelivery.failed_at) }}</span>
                <p style="color:#dc2626;font-size:12px;margin:4px 0 0">{{ selectedDelivery.failure_reason }}</p>
              </div>
            </div>
          </div>

          <!-- Dispatch Offers -->
          <template v-if="selectedDelivery.offers?.length">
            <div class="gl-detail-section-title" style="margin-top:20px">Dispatch Offers ({{ selectedDelivery.offers.length }})</div>
            <div class="gl-tbl-scroll" style="margin-top:10px">
              <table class="gl-tbl">
                <thead>
                  <tr><th>Rider</th><th>Attempt</th><th>Status</th><th>Distance</th><th>Offered</th><th>Responded</th></tr>
                </thead>
                <tbody>
                  <tr v-for="offer in selectedDelivery.offers" :key="offer.id">
                    <td>{{ offer.rider?.name || '#' + offer.rider_id }}</td>
                    <td style="text-align:center">{{ offer.attempt }}</td>
                    <td><span class="gl-badge" :class="offerBadgeClass(offer.status)">{{ offer.status }}</span></td>
                    <td>{{ offer.distance_km ? offer.distance_km + ' km' : '—' }}</td>
                    <td class="gl-tbl-sub">{{ formatDateTime(offer.offered_at) }}</td>
                    <td class="gl-tbl-sub">{{ offer.responded_at ? formatDateTime(offer.responded_at) : '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminDeliveryApi } from '@/services/superAdminApi';

export default {
  name: 'DeliveryList',
  data() {
    return {
      loading: false,
      deliveries: [],
      pagination: { currentPage: 1, lastPage: 1, total: 0 },
      filters: { status: '', provider: '', search: '' },
      selectedDelivery: null,
      searchTimeout: null
    };
  },
  computed: {
    paginationPages() {
      const pages = [], cur = this.pagination.currentPage, last = this.pagination.lastPage;
      if (last <= 7) { for (let i = 1; i <= last; i++) pages.push(i); }
      else if (cur <= 3) { pages.push(1, 2, 3, 4, '...', last); }
      else if (cur >= last - 2) { pages.push(1, '...', last - 3, last - 2, last - 1, last); }
      else { pages.push(1, '...', cur - 1, cur, cur + 1, '...', last); }
      return pages;
    }
  },
  created() { this.fetchDeliveries(); },
  methods: {
    async fetchDeliveries(page = 1) {
      this.loading = true;
      try {
        const r = await superAdminDeliveryApi.getDeliveries({ page, ...this.filters });
        if (r.data.success) {
          this.deliveries = r.data.data;
          this.pagination = { currentPage: r.data.meta.current_page, lastPage: r.data.meta.last_page, total: r.data.meta.total };
        }
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchDeliveries(), 500);
    },
    goToPage(page) {
      if (page >= 1 && page <= this.pagination.lastPage) this.fetchDeliveries(page);
    },
    viewDelivery(d) { this.selectedDelivery = d; },
    formatCurrency(v) { return v ? parseFloat(v).toFixed(2) : '0.00'; },
    formatDate(d) { return d ? new Date(d).toLocaleString('en-MY', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'; },
    formatDateTime(d) { return d ? new Date(d).toLocaleString('en-MY', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'; },
    formatStatus(s) { return s ? s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : ''; },
    statusBadgeClass(s) {
      const m = { pending: 'gl-badge-yellow', assigned: 'gl-badge-info', picking_up: 'gl-badge-info', picked_up: 'gl-badge-blue', on_the_way: 'gl-badge-blue', arrived: 'gl-badge-green', delivered: 'gl-badge-green', failed: 'gl-badge-red', cancelled: 'gl-badge-gray' };
      return m[s] || 'gl-badge-gray';
    },
    providerBadgeClass(p) {
      const m = { inhouse: 'gl-badge-teal', lalamove: 'gl-badge-yellow', grab: 'gl-badge-green' };
      return m[p] || 'gl-badge-gray';
    },
    offerBadgeClass(s) {
      const m = { offered: 'gl-badge-info', accepted: 'gl-badge-green', declined: 'gl-badge-gray', expired: 'gl-badge-yellow', voided: 'gl-badge-gray' };
      return m[s] || 'gl-badge-gray';
    }
  }
};
</script>

<style>
.gl-page { padding: 24px; max-width: 1400px; }
.gl-page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; gap: 16px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0 0 2px; letter-spacing: -.3px; }
.gl-page-sub { font-size: 13px; color: #6b7f7b; margin: 0; }
.gl-filter-bar { display: flex; gap: 10px; align-items: center; margin-bottom: 16px; flex-wrap: wrap; }
.gl-filter-search { position: relative; flex: 1; min-width: 200px; }
.gl-filter-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; pointer-events: none; }
.gl-filter-input { width: 100%; padding: 9px 12px 9px 36px; border: 1.5px solid #e0e7e5; border-radius: 10px; font-size: 13.5px; color: #111827; background: #fff; outline: none; box-sizing: border-box; }
.gl-filter-input:focus { border-color: #900C27; }
.gl-filter-select { padding: 9px 12px; border: 1.5px solid #e0e7e5; border-radius: 10px; font-size: 13.5px; color: #111827; background: #fff; outline: none; cursor: pointer; }
.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; }
.gl-tbl-scroll { overflow-x: auto; }
.gl-tbl { width: 100%; border-collapse: collapse; }
.gl-tbl th { padding: 11px 14px; font-size: 11px; font-weight: 600; color: #6b7f7b; text-transform: uppercase; letter-spacing: .6px; background: #f8faf9; border-bottom: 1px solid #eaedec; white-space: nowrap; }
.gl-tbl td { padding: 13px 14px; font-size: 13.5px; color: #111827; border-bottom: 1px solid #f5f7f6; vertical-align: middle; }
.gl-tbl tbody tr:last-child td { border-bottom: none; }
.gl-tbl tbody tr:hover td { background: #f8faf9; }
.gl-tbl-sub { font-size: 11.5px; color: #9ca3af; }
.gl-id-badge { font-family: 'SF Mono', monospace; font-size: 12px; font-weight: 700; color: #900C27; background: #e8f4f1; padding: 2px 6px; border-radius: 5px; white-space: nowrap; }
.gl-badge { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 20px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; white-space: nowrap; }
.gl-badge-green { background: #dcfce7; color: #15803d; }
.gl-badge-gray { background: #f1f5f9; color: #64748b; }
.gl-badge-red { background: #fee2e2; color: #dc2626; }
.gl-badge-yellow { background: #fef9c3; color: #92400e; }
.gl-badge-info { background: #cffafe; color: #0e7490; }
.gl-badge-blue { background: #dbeafe; color: #1d4ed8; }
.gl-badge-teal { background: #e8f4f1; color: #900C27; }
.gl-icon-btn { width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid #e0e7e5; background: #fff; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; color: #6b7f7b; }
.gl-icon-btn:hover { border-color: #900C27; color: #900C27; background: #e8f4f1; }
.gl-pager { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-top: 1px solid #f0f3f2; flex-wrap: wrap; gap: 10px; }
.gl-pager-info { font-size: 12.5px; color: #9ca3af; }
.gl-pager-btns { display: flex; gap: 4px; }
.gl-pager-btn { min-width: 32px; height: 32px; padding: 0 8px; border: 1.5px solid #e0e7e5; border-radius: 8px; background: #fff; font-size: 13px; color: #374151; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.gl-pager-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-pager-btn:disabled { opacity: .4; cursor: not-allowed; }
.gl-pager-btn-active { background: #900C27; border-color: #900C27; color: #fff !important; font-weight: 600; }
.gl-pager-btn-dots { border-color: transparent; background: transparent; cursor: default; }
.gl-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 24px; }
.gl-empty-icon { width: 64px; height: 64px; border-radius: 16px; background: #f0f3f2; display: flex; align-items: center; justify-content: center; color: #9ca3af; margin-bottom: 16px; }
.gl-empty-title { font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 4px; }
.gl-empty-sub { font-size: 13px; color: #9ca3af; }
.gl-skel { background: linear-gradient(90deg, #f0f3f2 25%, #e8edeb 50%, #f0f3f2 75%); background-size: 200% 100%; border-radius: 6px; animation: gl-shimmer 1.5s infinite; display: block; }
@keyframes gl-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.gl-skel-xs { width: 40px; height: 10px; }
.gl-skel-sm { width: 60px; height: 10px; }
.gl-skel-md { width: 100px; height: 10px; }
.gl-skel-badge { width: 60px; height: 22px; border-radius: 20px; background: #f0f3f2; animation: gl-shimmer 1.5s infinite; }
.gl-skel-btn { width: 32px; height: 32px; border-radius: 8px; background: #f0f3f2; animation: gl-shimmer 1.5s infinite; }
.gl-tbl-skel { padding: 0 4px; }
.gl-tbl-skel-head { display: flex; gap: 20px; padding: 12px 16px; border-bottom: 1px solid #eaedec; }
.gl-tbl-skel-row { display: flex; align-items: center; gap: 20px; padding: 14px 16px; border-bottom: 1px solid #f5f7f6; }
/* Mobile cards */
.gl-mob-list { display: none; flex-direction: column; }
.gl-mob-card { padding: 16px; border-bottom: 1px solid #f0f3f2; }
.gl-mob-card:last-child { border-bottom: none; }
.gl-dlv-mob-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; gap: 8px; }
.gl-dlv-mob-ids { display: flex; gap: 6px; flex-wrap: wrap; }
.gl-dlv-mob-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 12px; }
.gl-dlv-mob-cell {}
.gl-mob-label { font-size: 10.5px; color: #9ca3af; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 3px; }
.gl-mob-val { font-size: 13px; color: #111827; font-weight: 500; }
.gl-mob-val-em { color: #16a34a; font-weight: 700; }
.gl-mob-view-btn { width: 100%; padding: 9px; border: 1.5px solid #e0e7e5; border-radius: 9px; background: #fff; font-size: 13px; font-weight: 600; color: #374151; cursor: pointer; }
.gl-mob-view-btn:hover { border-color: #900C27; color: #900C27; }
/* Modal */
.gl-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1050; padding: 16px; }
.gl-modal { background: #fff; border-radius: 18px; width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.18); position: relative; }
.gl-modal-lg { max-width: 680px; }
.gl-modal-header { padding: 22px 56px 16px 24px; border-bottom: 1px solid #f0f3f2; position: sticky; top: 0; background: #fff; z-index: 1; }
.gl-modal-title { font-size: 16px; font-weight: 700; color: #111827; }
.gl-modal-sub { margin-top: 4px; }
.gl-modal-close { position: absolute; top: 16px; right: 16px; width: 32px; height: 32px; border-radius: 8px; border: 1px solid #eaedec; background: #fff; color: #6b7f7b; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.gl-modal-close:hover { background: #f8faf9; }
.gl-modal-body { padding: 20px 24px; }
.gl-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.gl-detail-section-title { font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .6px; margin-bottom: 10px; }
.gl-detail-rows { display: flex; flex-direction: column; gap: 8px; }
.gl-detail-row { display: flex; justify-content: space-between; align-items: center; font-size: 13px; gap: 8px; }
.gl-detail-row span:first-child { color: #9ca3af; }
.gl-detail-empty { font-size: 12.5px; color: #9ca3af; font-style: italic; }
.gl-timeline { display: flex; flex-direction: column; gap: 0; }
.gl-timeline-item { display: flex; align-items: flex-start; gap: 12px; padding: 8px 0; position: relative; }
.gl-timeline-item:not(:last-child)::before { content: ''; position: absolute; left: 7px; top: 26px; bottom: -8px; width: 2px; background: #f0f3f2; }
.gl-timeline-dot { width: 16px; height: 16px; border-radius: 50%; flex-shrink: 0; margin-top: 3px; }
.gl-dot-gray { background: #e5e7eb; }
.gl-dot-blue { background: #bfdbfe; }
.gl-dot-teal { background: #a7f3d0; }
.gl-dot-green { background: #bbf7d0; }
.gl-dot-red { background: #fecaca; }
.gl-timeline-content { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.gl-timeline-time { font-size: 12px; color: #9ca3af; }
/* Responsive */
@media (max-width: 767px) {
  .gl-page { padding: 16px; }
  .gl-page-title { font-size: 18px; }
  .gl-filter-bar { flex-direction: column; align-items: stretch; }
  .gl-filter-select { width: 100%; }
  .gl-tbl-scroll { display: none; }
  .gl-mob-list { display: flex; }
  .gl-dlv-mob-grid { grid-template-columns: 1fr 1fr; }
  .gl-pager { flex-direction: column; align-items: center; }
  .gl-modal { border-radius: 20px 20px 0 0; max-width: 100%; margin: auto 0 0; max-height: 90vh; }
  .gl-modal-overlay { align-items: flex-end; padding: 0; }
  .gl-detail-grid { grid-template-columns: 1fr; }
}
body.dark-only .gl-page-title { color: #f1f5f9; }
body.dark-only .gl-page-sub { color: #94a3b8; }
body.dark-only .gl-card { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-tbl th { background: #162220; color: #94a3b8; border-color: #2d3f3b; }
body.dark-only .gl-tbl td { color: #e2e8f0; border-color: #1e2a27; }
body.dark-only .gl-tbl tbody tr:hover td { background: #162220; }
body.dark-only .gl-filter-input,
body.dark-only .gl-filter-select { background: #1e2a27; border-color: #2d3f3b; color: #e2e8f0; }
body.dark-only .gl-icon-btn { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-pager-btn { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-mob-card { border-color: #2d3f3b; }
body.dark-only .gl-mob-val { color: #e2e8f0; }
body.dark-only .gl-mob-view-btn { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-modal,
body.dark-only .gl-modal-header { background: #1e2a27; }
body.dark-only .gl-modal-header { border-color: #2d3f3b; }
body.dark-only .gl-modal-title { color: #f1f5f9; }
body.dark-only .gl-modal-close { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-timeline-item:not(:last-child)::before { background: #2d3f3b; }
body.dark-only .gl-badge-green { background: rgba(22,163,74,.2); color: #4ade80; }
body.dark-only .gl-badge-gray { background: rgba(100,116,139,.2); color: #94a3b8; }
body.dark-only .gl-badge-red { background: rgba(220,38,38,.2); color: #f87171; }
body.dark-only .gl-badge-yellow { background: rgba(217,119,6,.2); color: #fbbf24; }
body.dark-only .gl-badge-info { background: rgba(14,116,144,.2); color: #67e8f9; }
body.dark-only .gl-badge-blue { background: rgba(37,99,235,.2); color: #60a5fa; }
body.dark-only .gl-badge-teal { background: rgba(144,12,39,.2); color: #4ade80; }
body.dark-only .gl-id-badge { background: rgba(144,12,39,.3); color: #5eead4; }
body.dark-only .gl-empty-title { color: #e2e8f0; }
body.dark-only .gl-empty-icon { background: #162220; }
</style>
